import type { ReactNode } from 'react';

import type { Policy, PolicyPendingIssuance } from '@/shared/api/resources';
import { cn } from '@/shared/lib/cn';
import { formatCivilDate, formatDate, formatMoney } from '@/shared/lib/format';
import { CopyButton } from '@/shared/ui/CopyButton';
import type { Column } from '@/shared/ui/DataTable';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import {
  TabelaAjustavel,
  type EncaixeDaTabela,
  type LargurasDaTabela,
} from '@/shared/ui/TabelaAjustavel';

import { MenuDeAcoes, type AcaoDoMenu } from '../users/MenuDeAcoes';

import {
  corretorDaApolice,
  documentoOuVazio,
  NAO_DEFINIDO,
  numeroDaApolice,
  ouNaoDefinido,
  seguradoDaApolice,
  simNao,
  tomadorDaApolice,
  vigenciaDe,
} from './apolice';

/**
 * A carteira vista pela corretora sem barra de rolagem (04/11/2026).
 *
 * A lista de apolices tem dezesseis colunas e rolava para o lado em qualquer tela comum.
 * Na sessao da corretora, como na lista de cotacoes dela, a largura medida escolhe a
 * disposicao, e o que nao cabe numa coluna propria vai para a segunda linha da celula:
 * a cotacao e a referencia da seguradora embaixo do numero, o segurado embaixo do
 * tomador (no titulo, os documentos), o emissor e o corretor embaixo da modalidade, o
 * premio embaixo da IS, os dias e a emissao embaixo da vigencia, o endosso e o cosseguro
 * embaixo do status. As acoes vao para o menu da linha: os mesmos downloads do "Baixar
 * arquivos", "Detalhes da apolice" e "Solicitar endosso". A lista da seguradora continua
 * a de antes.
 */

/** Uma linha da lista: a apolice emitida, ou a operacao que aguarda emissao ("S/N"). */
export type LinhaDaCarteira =
  | { tipo: 'apolice'; apolice: Policy }
  | { tipo: 'pendente'; pendente: PolicyPendingIssuance };

/** Fluida a partir de 900 px de area; com tomador e segurado em colunas proprias, 1340. */
const largurasDaCarteira: LargurasDaTabela = { ampla: 1340, reunida: 900 };

function Linha({
  children,
  className,
  titulo,
}: {
  children: ReactNode;
  className?: string;
  titulo?: string | undefined;
}) {
  return (
    <span
      title={titulo ?? (typeof children === 'string' ? children : undefined)}
      className={cn('block truncate', className)}
    >
      {children}
    </span>
  );
}

function Detalhe({ children, titulo }: { children: ReactNode; titulo?: string }) {
  return (
    <Linha className="text-2xs text-ink-500" titulo={titulo}>
      {children}
    </Linha>
  );
}

function Celula({
  linha1,
  linha2,
  titulo,
}: {
  linha1: ReactNode;
  linha2?: string | undefined;
  titulo?: string | undefined;
}) {
  return (
    <div className="min-w-0" title={titulo}>
      {typeof linha1 === 'string' ? <Linha>{linha1}</Linha> : linha1}
      {linha2 ? <Detalhe titulo={titulo ?? linha2}>{linha2}</Detalhe> : null}
    </div>
  );
}

// --- O que cada linha diz, da apolice ou da operacao em emissao ---------------------

function numeroDaCotacao(linha: LinhaDaCarteira): string | null {
  return linha.tipo === 'pendente'
    ? linha.pendente.proposal_number
    : (linha.apolice.proposal_number ?? null);
}

function tomadorDe(linha: LinhaDaCarteira): { nome: string; documento: string } {
  return linha.tipo === 'pendente'
    ? {
        nome: linha.pendente.policyholder.label,
        documento: linha.pendente.policyholder_document,
      }
    : tomadorDaApolice(linha.apolice);
}

function seguradoDe(
  linha: LinhaDaCarteira,
): { nome: string; documento: string } | null {
  if (linha.tipo === 'apolice') return seguradoDaApolice(linha.apolice);
  return linha.pendente.insured
    ? { nome: linha.pendente.insured.label, documento: linha.pendente.insured.sublabel }
    : null;
}

function comDocumento(parte: { nome: string; documento: string } | null): string {
  if (!parte) return NAO_DEFINIDO;
  const documento = documentoOuVazio(parte.documento);
  return documento
    ? `${ouNaoDefinido(parte.nome)} - ${documento}`
    : ouNaoDefinido(parte.nome);
}

function emissorECorretor(linha: LinhaDaCarteira): string {
  const corretor =
    linha.tipo === 'pendente'
      ? (linha.pendente.broker?.label ?? 'Operacao direta')
      : (corretorDaApolice(linha.apolice)?.nome ?? 'Operacao direta');
  const emissor = linha.tipo === 'pendente' ? NAO_DEFINIDO : linha.apolice.issuer.label;
  return `${emissor} · ${corretor}`;
}

function vigenciaEmissao(linha: LinhaDaCarteira): { periodo: string; detalhe: string } {
  if (linha.tipo === 'pendente') {
    return {
      periodo: vigenciaDe(linha.pendente.coverage_start, linha.pendente.coverage_end),
      detalhe: 'Ainda sem emissao',
    };
  }
  const apolice = linha.apolice;
  const dias = apolice.days_to_expiry;
  // A emissao vem primeiro: os dias sao o que a coluna corta quando estreita, e o fim da
  // vigencia ja esta na linha de cima.
  const restantes =
    apolice.status === 'cancelada'
      ? ''
      : dias < 0
        ? ` · vencida ha ${Math.abs(dias)} d`
        : ` · ${dias} d restantes`;
  return {
    periodo: `${formatCivilDate(apolice.coverage_start)} - ${formatCivilDate(apolice.coverage_end)}`,
    detalhe: `Emitida em ${formatDate(apolice.issued_at)}${restantes}`,
  };
}

function corDaVigencia(linha: LinhaDaCarteira): string {
  if (linha.tipo === 'pendente') return 'text-ink-700';
  const { status, days_to_expiry: dias } = linha.apolice;
  if (status === 'cancelada') return 'text-ink-400';
  if (dias < 0) return 'text-danger-700';
  return dias <= 60 ? 'text-warning-700' : 'text-ink-700';
}

// --- Colunas --------------------------------------------------------------------------

function colunasDaCarteira(
  encaixe: EncaixeDaTabela,
  acoes: AcoesDaCarteira,
): Column<LinhaDaCarteira>[] {
  const separadas = encaixe === 'ampla';
  const partes: Column<LinhaDaCarteira>[] = separadas
    ? [
        {
          key: 'tomador',
          header: 'Tomador',
          render: (linha) => {
            const tomador = tomadorDe(linha);
            return (
              <Celula
                linha1={ouNaoDefinido(tomador.nome)}
                linha2={documentoOuVazio(tomador.documento) || undefined}
              />
            );
          },
        },
        {
          key: 'segurado',
          header: 'Segurado',
          render: (linha) => {
            const segurado = seguradoDe(linha);
            return segurado ? (
              <Celula
                linha1={ouNaoDefinido(segurado.nome)}
                linha2={documentoOuVazio(segurado.documento) || undefined}
              />
            ) : (
              <span className="text-ink-400">{NAO_DEFINIDO}</span>
            );
          },
        },
      ]
    : [
        {
          key: 'tomador',
          header: 'Tomador',
          detail: 'Segurado',
          render: (linha) => (
            <div className="min-w-0">
              <Linha titulo={comDocumento(tomadorDe(linha))}>
                {ouNaoDefinido(tomadorDe(linha).nome)}
              </Linha>
              <Detalhe titulo={comDocumento(seguradoDe(linha))}>
                {seguradoDe(linha)?.nome ?? NAO_DEFINIDO}
              </Detalhe>
            </div>
          ),
        },
      ];

  return [
    {
      key: 'apolice',
      header: 'Apolice',
      detail: 'Cotacao e ref. da seguradora',
      width: '11rem',
      render: (linha) => {
        const cotacao = numeroDaCotacao(linha) ?? NAO_DEFINIDO;
        const referencia =
          linha.tipo === 'apolice' ? (linha.apolice.insurer_reference ?? '') : '';
        const detalhe = `Cotacao ${cotacao}${referencia ? ` · Ref. ${referencia}` : ''}`;
        return (
          <div className="min-w-0">
            {linha.tipo === 'pendente' ? (
              <Linha className="font-medium text-ink-500">S/N</Linha>
            ) : (
              <span className="flex min-w-0 items-center gap-1">
                <Linha className="font-medium text-ink-900">
                  {numeroDaApolice(linha.apolice)}
                </Linha>
                <CopyButton
                  value={numeroDaApolice(linha.apolice)}
                  label="Copiar numero da apolice"
                />
              </span>
            )}
            <Detalhe>{detalhe}</Detalhe>
          </div>
        );
      },
    },
    ...partes,
    {
      key: 'modalidade',
      header: 'Modalidade',
      detail: 'Emissor e corretor',
      width: '13%',
      render: (linha) => (
        <Celula
          linha1={
            linha.tipo === 'pendente'
              ? linha.pendente.modality.label
              : linha.apolice.modality.label
          }
          linha2={emissorECorretor(linha)}
        />
      ),
    },
    {
      key: 'valor',
      header: 'IS',
      detail: 'Premio da apolice',
      align: 'right',
      width: '8.5rem',
      render: (linha) => {
        const valor =
          linha.tipo === 'pendente'
            ? linha.pendente.insured_amount
            : linha.apolice.insured_amount;
        const premio =
          linha.tipo === 'pendente' ? linha.pendente.premium : linha.apolice.premium;
        return (
          <Celula
            linha1={<Linha className="tabular-nums">{formatMoney(valor)}</Linha>}
            linha2={formatMoney(premio)}
          />
        );
      },
    },
    {
      key: 'vigencia',
      header: 'Vigencia atual',
      detail: 'Dias e emissao',
      width: '11rem',
      render: (linha) => {
        const { periodo, detalhe } = vigenciaEmissao(linha);
        return (
          <Celula
            linha1={
              <Linha className={cn('text-xs tabular-nums', corDaVigencia(linha))}>
                {periodo}
              </Linha>
            }
            linha2={detalhe}
          />
        );
      },
    },
    {
      key: 'situacao',
      header: 'Status',
      detail: 'Endosso e cosseguro',
      width: '9.5rem',
      render: (linha) => {
        const extras =
          linha.tipo === 'pendente'
            ? 'Endosso -- · Cosseguro --'
            : `Endosso ${simNao((linha.apolice.endorsement_count ?? 0) > 0)} · Cosseguro ${simNao(linha.apolice.has_coinsurance)}`;
        return (
          <div className="min-w-0">
            <StatusBadge
              kind="apolice"
              value={
                linha.tipo === 'pendente' ? 'aguardando_emissao' : linha.apolice.status
              }
            />
            <Detalhe>{extras}</Detalhe>
          </div>
        );
      },
    },
    {
      key: 'acoes',
      header: 'Acoes',
      width: '7.5rem',
      render: (linha) => (
        <MenuDeAcoes
          titulo={
            linha.tipo === 'pendente'
              ? `Acoes da cotacao ${linha.pendente.proposal_number ?? linha.pendente.operation_number}`
              : `Acoes da apolice ${numeroDaApolice(linha.apolice)}`
          }
          acoes={acoes.acoesDe(linha)}
          ocupado={acoes.ocupada(linha)}
        />
      ),
    },
  ];
}

// --- Acoes ----------------------------------------------------------------------------

/** As acoes que a pagina da a cada linha, alem dos downloads. */
export interface AcoesDaCarteira {
  acoesDe: (linha: LinhaDaCarteira) => AcaoDoMenu[];
  ocupada: (linha: LinhaDaCarteira) => boolean;
}

export function ListaDeApolicesDaCorretora({
  linhas,
  keyOf,
  loading,
  empty,
  acoes,
}: {
  linhas: LinhaDaCarteira[];
  keyOf: (linha: LinhaDaCarteira) => string;
  loading: boolean;
  empty: ReactNode;
  acoes: AcoesDaCarteira;
}) {
  return (
    <TabelaAjustavel
      colunas={(encaixe) => colunasDaCarteira(encaixe, acoes)}
      larguras={largurasDaCarteira}
      items={linhas}
      keyOf={keyOf}
      loading={loading}
      empty={empty}
    />
  );
}
